import { generatePath } from 'react-router';
import { sourceKeyMapping, paths } from '@AppRoutes/constants';
import { generateHeatmapPath, generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import * as AuthUtils from '@Services/Auth/AuthUtils';
import AppStorage from '@Services/AppStorage';
import Breadcrumb from '@Services/Breadcrumb';
import History from '@Services/History';
import loginConstants from '@Services/Login/LoginConstants';
import ResultsInfo from '@Services/ResultsInfo';
import each from 'lodash/each';
import findLastIndex from 'lodash/findLastIndex';
import extend from 'lodash/extend';
import { startGenAIContentProcessingTimer } from '@Containers/Summary/analytics';

let visitedStates = [];

const getTextQueryFromStorage = (searchId) => {
  const semanticQueryBySearchId = AppStorage.instance.getFromSessionStorage('qbSemanticQueryBySearchId') || {};

  if (!searchId || !semanticQueryBySearchId?.[searchId]) {
    return '';
  }

  const textQuery = semanticQueryBySearchId[searchId];
  const remainingQueries = { ...semanticQueryBySearchId };
  delete remainingQueries[searchId];
  AppStorage.instance.setToSessionStorage('qbSemanticQueryBySearchId', remainingQueries);
  return textQuery;
};

const withSemanticFallback = (item) => {
  const textQuery = item?.textQuery || getTextQueryFromStorage(item?.searchId);

  if (!textQuery) {
    return item;
  }

  return {
    ...item,
    semanticSearchIncluded: true,
    semanticResultName: item?.semanticResultName || item?.resultName,
    textQuery,
  };
};

export const init = () => {
  const currentUser = AuthUtils?.getUser() || {};
  if (!currentUser.history) {
    Object.assign(currentUser, {
      history: {
        breadcrumbs: {},
        viewedResultsParams: {},
        recentSearches: [],
        savedSearches: [],
      },
    });
  }
  AuthUtils.updateUser(currentUser);
};

export const addState = (state, params) => {
  const stateObj = {
    name: state,
    params,
  };
  visitedStates.push(stateObj);
};

// Updated for React routes
export const getStatePathName = (state) => {
  if (!state) return null;

  const _statePath = Object.keys(sourceKeyMapping).find((_path) => sourceKeyMapping[_path] === state.name);
  if (_statePath === paths.RESULTS) {
    return generateResultsPath(state.params);
  }
  if (_statePath === paths.HEATMAP) {
    return generateHeatmapPath({ ...state.params, isHeatmap: true });
  }
  return generatePath(_statePath, state.params);
};

export const getLastVisitedState = (...args) => {
  if (!visitedStates.length) {
    return null;
  }

  let lastStateIndex = null;
  let lastState = null;

  each(args, (proposedState) => {
    const proposedStateIndex = findLastIndex(visitedStates, (state) => state && state.name === proposedState);

    if (proposedStateIndex >= 0 && (lastStateIndex === null || proposedStateIndex > lastStateIndex)) {
      lastStateIndex = proposedStateIndex;
      lastState = visitedStates[lastStateIndex];
    }
  });
  return (
    lastState && {
      ...lastState,
      pathName: getStatePathName(lastState),
    }
  );
};

export const getLastVisitedStateForLogin = () => getLastVisitedState(...loginConstants.STATE_WHITELIST);

export const viewHistory = (item) => {
  if (item.resultSize) {
    History.restoreHistory(item)
      // eslint-disable-next-line no-shadow
      .then((item) => {
        const historyItem = {
          ...withSemanticFallback(item),
          isHistoryBreadcrumb: true,
        };
        Breadcrumb.resetBreadcrumbs();
        ResultsInfo.clearResultsInfo();
        const params = {
          databaseId: historyItem.databaseId,
          context: historyItem.context,
          resultName: historyItem.resultName,
          searchId: historyItem.searchId,
          pageNumber: 1,
          sort: '',
          hitSetKey: historyItem.hitSetKey,
        };
        Breadcrumb.activateBreadcrumb(historyItem.resultName, historyItem.databaseId);
        Breadcrumb.createBreadcrumb(historyItem, historyItem.resultSize, historyItem.filters)
          .then(() => {
            if (historyItem.isHeatmapShown) {
              extend(params, {
                sortedMatrixName: historyItem.sortedMatrixName,
                index: historyItem.index || null,
                isRow: historyItem.isRow || null,
                ascending: historyItem.ascending || null,
                fromContext: historyItem.fromContext,
                context: 'dpitems',
                hitSetKey: historyItem.hitSetKey,
                resultName: historyItem.heatmapResultName,
                searchId: historyItem.searchId,
                order: historyItem.order,
                sort: historyItem.sort,
              });
            }

            if (historyItem.semanticSearchIncluded) {
              extend(params, {
                semanticSearchIncluded: true,
                semanticResultName: historyItem.semanticResultName || '',
                textQuery: historyItem.textQuery || '',
              });
            }

            Breadcrumb.updateActiveParams(null); // reset active resultName
            const pathName = historyItem.isHeatmapShown ? generateHeatmapPath(params) : generateResultsPath(params);

            if (params.semanticSearchIncluded) {
              startGenAIContentProcessingTimer();
            }

            history.push({
              pathname: pathName,
              state: { ...params, order: historyItem.order, sort: historyItem.sort },
            });
          })
          .catch(Logger.error);
      })
      .catch(Logger.error);
  }
};

export const softClear = () => {
  visitedStates.length = 1;
};

export const hardClear = () => {
  visitedStates = [];
};

export const importVisitedStates = (states) => {
  visitedStates = states;
};
